"use client";

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import type { PieceSymbol, Square } from "chess.js";
import { MoveList } from "@/components/board/MoveList";
import { BoardView } from "@/components/board/BoardView";
import { PieceArt } from "@/components/board/PieceArt";
import type { BoardRules, MoveIntent } from "@/components/board/interaction";
import { PlayBoard, ClubPlayer, ReplayControls } from "@/components/play/PlayBoard";
import { PlayIcon } from "@/components/play/PlayIcon";
import { usePlayPreferences } from "@/components/play/preferences";
import { usePlayStore } from "@/components/play/store";
import { useAppearance } from "@/components/site/AppearanceProvider";
import { getVariantEngine } from "@/lib/chess/engine";
import { toPgn, START_FEN } from "@/lib/chess/game";
import { playMoveSound } from "@/lib/sound";
import { PLAYABLE_VARIANTS, VARIANT_LEVELS, getPlayableVariant, getVariantLevel, type PlayableVariant } from "@/lib/variants/config";
import { checksRemaining, findKing, parsePockets, sideToMove, type Pockets } from "@/lib/variants/fen";
import { describePosition, intentToUci, loadFfish, playVariantMove, variantStartFen, type Ffish, type VariantPosition } from "@/lib/variants/rules";
import { chooseVariantMove } from "@/lib/variants/bot";
import { useVariantStore, variantTakebackLength } from "./store";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
function sound(kind: Parameters<typeof playMoveSound>[0]) { if (usePlayPreferences.getState().sound) playMoveSound(kind); }

const ZONE_TINT = "rgba(215, 154, 118, 0.32)";
const POCKET_ORDER: PieceSymbol[] = ["q", "r", "b", "n", "p"];
const PIECE_NAMES: Record<PieceSymbol, string> = { p: "pawn", n: "knight", b: "bishop", r: "rook", q: "queen", k: "king" };
const PGN_NAMES: Record<string, string> = { chess960: "Chess960", crazyhouse: "Crazyhouse", atomic: "Atomic", threecheck: "Three-check", kingofthehill: "King of the Hill", antichess: "Antichess", horde: "Horde", racingkings: "Racing Kings" };

/** Move numbering helpers read the standard six FEN fields, so drop pockets and check counters first. */
function plainFen(fen: string) {
  const fields = fen.split(" ").filter((f) => !/^\d+\+\d+$/.test(f));
  fields[0] = fields[0]?.replace(/\[.*\]$/, "");
  return fields.join(" ");
}

function Pocket({ pockets, color, selectable, selected, onSelect }: { pockets: Pockets | null; color: "w" | "b"; selectable: boolean; selected: PieceSymbol | null; onSelect: (p: PieceSymbol | null) => void }) {
  const { appearance } = useAppearance();
  if (!pockets) return null;
  const held = POCKET_ORDER.filter((p) => (pockets[color][p] ?? 0) > 0);
  const side = color === "w" ? "White" : "Black";
  return <div className="variant-pocket" role="group" aria-label={`${side}’s pocket`}>
    {held.length === 0 && <span className="variant-pocket-empty">{side}’s pocket is empty</span>}
    {held.map((p) => {
      const count = pockets[color][p] ?? 0;
      const label = `${count} ${PIECE_NAMES[p]}${count > 1 ? "s" : ""}`;
      return selectable
        ? <button key={p} type="button" aria-pressed={selected === p} aria-label={`Drop a ${PIECE_NAMES[p]} (${label} in pocket)`} onClick={() => onSelect(selected === p ? null : p)}><span aria-hidden="true"><PieceArt type={p} color={color} finish={appearance.pieces} style={appearance.style} /></span>{count > 1 && <small aria-hidden="true">{count}</small>}</button>
        : <span key={p} className="variant-pocket-piece" aria-label={label}><span aria-hidden="true"><PieceArt type={p} color={color} finish={appearance.pieces} style={appearance.style} /></span>{count > 1 && <small aria-hidden="true">{count}</small>}</span>;
    })}
  </div>;
}

function useFfish() {
  const [ff, setFf] = useState<Ffish | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    loadFfish().then((m) => { if (live) { setFf(m); setError(null); } }).catch(() => { if (live) setError("The variant rules couldn’t load. Check your connection and try again."); });
    return () => { live = false; };
  }, [attempt]);
  return { ff, error, retry: () => { setError(null); setAttempt((n) => n + 1); } };
}

function VariantSetup({ ff, ffError, onRetry }: { ff: Ffish | null; ffError: string | null; onRetry: () => void }) {
  const s = useVariantStore();
  const prefs = usePlayPreferences();
  const variant = getPlayableVariant(s.variantId) ?? PLAYABLE_VARIANTS[0];
  const [levelId, setLevelId] = useState(s.levelId);
  const [index960, setIndex960] = useState(() => Math.floor(Math.random() * 960));
  const [indexText, setIndexText] = useState("");
  const previewFen = useMemo(() => {
    if (!ff) return null;
    try { return variantStartFen(ff, variant, index960); } catch { return null; }
  }, [ff, variant, index960]);
  const previewRules = useMemo<BoardRules>(() => ({ legal: [] }), []);
  const tints = useMemo(() => Object.fromEntries((variant.zone?.squares ?? []).map((sq) => [sq, ZONE_TINT])) as Partial<Record<Square, string>>, [variant]);
  const record = s.records[variant.id];

  const start = () => {
    if (!previewFen) return;
    const color = prefs.preferredColor === "random" ? (Math.random() < 0.5 ? "w" : "b") : prefs.preferredColor;
    s.newGame({ variantId: variant.id, startFen: previewFen, chess960Index: variant.chess960 ? index960 : null, color, levelId });
  };

  return <section className="club-play variant-setup" aria-labelledby="variant-setup-title">
    <div className="variant-setup-copy">
      <Link href="/variants" className="variant-back"><PlayIcon name="back" /> All variants</Link>
      <p className="eyebrow">Play a variant</p>
      <h1 id="variant-setup-title">Same pieces. New rules.</h1>
      <p className="variant-lede">Choose a variant, pick your opponent, and play on a 2D or 3D board. Every game is checked by Fairy-Stockfish’s rules engine, and the bot is a multi-variant Stockfish.</p>

      <fieldset className="variant-choice-grid">
        <legend>Variant</legend>
        {PLAYABLE_VARIANTS.map((v) => {
          const r = s.records[v.id];
          return <label key={v.id} className="variant-choice">
            <input type="radio" name="variant" checked={v.id === variant.id} onChange={() => s.chooseVariant(v.id)} />
            <span><strong>{v.name}</strong><small>{v.goal}</small>{r && <em>{r.w}W · {r.d}D · {r.l}L</em>}</span>
          </label>;
        })}
      </fieldset>

      <fieldset className="variant-levels">
        <legend>Opponent</legend>
        <div>{VARIANT_LEVELS.map((l) => <label key={l.id}><input type="radio" name="level" checked={levelId === l.id} onChange={() => setLevelId(l.id)} /><span><strong>{l.name}</strong><small>{l.blurb}</small></span></label>)}</div>
      </fieldset>

      <fieldset className="variant-levels variant-colors">
        <legend>Your pieces</legend>
        <div>{([["w", "White"], ["random", "Surprise me"], ["b", "Black"]] as const).map(([value, label]) => <label key={value}><input type="radio" name="color" checked={prefs.preferredColor === value} onChange={() => prefs.update({ preferredColor: value })} /><span><strong>{label}</strong></span></label>)}</div>
      </fieldset>

      {variant.chess960 && <div className="variant-960">
        <label htmlFor="variant-960-index">Starting position <span>0–959 · 518 is the standard setup</span></label>
        <div>
          <input id="variant-960-index" inputMode="numeric" value={indexText} placeholder={String(index960)} onChange={(e) => {
            setIndexText(e.target.value);
            const n = Number(e.target.value);
            if (e.target.value.trim() !== "" && Number.isInteger(n) && n >= 0 && n <= 959) setIndex960(n);
          }} />
          <button type="button" className="club-secondary" onClick={() => { setIndexText(""); setIndex960(Math.floor(Math.random() * 960)); }}><PlayIcon name="spark" /> Shuffle</button>
        </div>
      </div>}

      {ffError && <div className="club-error" role="alert"><p>{ffError}</p><button className="club-secondary" onClick={onRetry}>Try again</button></div>}
      <div className="variant-start">
        <button className="club-primary" onClick={start} disabled={!previewFen}>{previewFen ? <>Play {variant.name} <PlayIcon name="arrow" /></> : "Loading the rules…"}</button>
        <Link href={`/variants/${variant.slug}`} className="variant-rules-link">How {variant.name} works <PlayIcon name="arrow" /></Link>
      </div>
      {record && <p className="variant-record">Your {variant.name} record: {record.w} won, {record.d} drawn, {record.l} lost.</p>}
    </div>
    <div className="variant-setup-board">
      {previewFen ? <BoardView fen={previewFen} view="3d" rules={previewRules} tints={tints} movable="none" label={`${variant.name} starting position`} orientation={prefs.preferredColor === "b" ? "black" : "white"} /> : <div className="board-loading"><span className="loading-orbit" />Setting up the board…</div>}
      <ul className="variant-reminders">{variant.reminders.map((r) => <li key={r}>{r}</li>)}</ul>
    </div>
  </section>;
}

export function VariantPlayClient() {
  const s = useVariantStore();
  const searchParams = useSearchParams();
  const hydrated = useSyncExternalStore((cb) => useVariantStore.persist.onFinishHydration(cb), () => useVariantStore.persist.hasHydrated(), () => false);
  const { ff, error: ffError, retry: retryFfish } = useFfish();
  const [engineError, setEngineError] = useState<string | null>(null);
  const [engineAttempt, setEngineAttempt] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [drop, setDrop] = useState<PieceSymbol | null>(null);
  const [resignConfirm, setResignConfirm] = useState(false);

  useEffect(() => {
    void usePlayPreferences.persist.rehydrate();
    void usePlayStore.persist.rehydrate();
    void useVariantStore.persist.rehydrate();
  }, []);
  // A link such as /variants/play?v=atomic preselects the variant, without abandoning a game in progress.
  useEffect(() => {
    if (!hydrated) return;
    const wanted = getPlayableVariant(searchParams.get("v"));
    const current = useVariantStore.getState();
    if (wanted && current.status !== "playing") { current.chooseVariant(wanted.id); if (current.status === "over") current.backToSetup(); }
  }, [hydrated, searchParams]);

  const variant: PlayableVariant = getPlayableVariant(s.variantId) ?? PLAYABLE_VARIANTS[0];
  const level = getVariantLevel(s.levelId);
  const uciMoves = useMemo(() => s.moves.map((m) => m.uci), [s.moves]);
  const live = useMemo<VariantPosition | null>(() => {
    if (!ff || s.status === "setup" || !s.startFen) return null;
    try { return describePosition(ff, variant, s.startFen, uciMoves); } catch { return null; }
  }, [ff, variant, s.status, s.startFen, uciMoves]);
  const broken = !!ff && s.status !== "setup" && !!s.startFen && live === null;

  const ply = Math.min(s.cursor ?? s.moves.length, s.moves.length);
  const viewFen = ply === 0 ? s.startFen : s.moves[ply - 1].fen;
  const botColor = s.playerColor === "w" ? "b" : "w";
  const playerTurn = s.status === "playing" && live?.turn === s.playerColor;
  const thinking = hydrated && s.status === "playing" && live?.turn === botColor && !engineError;
  const opponent = `${level.name} bot`;
  const [prevFen, setPrevFen] = useState(viewFen);
  if (prevFen !== viewFen) { setPrevFen(viewFen); setDrop(null); }

  useEffect(() => {
    if (!hydrated || !ff || !live || s.status !== "playing" || live.turn !== botColor) return;
    const controller = new AbortController();
    const gameId = s.gameId, startFen = s.startFen, moves = uciMoves, started = performance.now();
    void (async () => {
      // The search bot runs on this thread; let the player's move finish animating first.
      if (variant.engine.kind === "search") await sleep(380);
      if (controller.signal.aborted) return;
      const uci = await chooseVariantMove(variant, { startFen, moves, fen: live.fen }, level, { ff, engine: getVariantEngine }, Math.random, controller.signal);
      if (controller.signal.aborted) return;
      const [lo, hi] = level.thinkMs;
      const wait = lo + Math.random() * (hi - lo) - (performance.now() - started);
      if (wait > 0) await sleep(wait);
      const current = useVariantStore.getState();
      if (controller.signal.aborted || current.gameId !== gameId || current.status !== "playing" || current.moves.length !== moves.length) return;
      const played = playVariantMove(ff, variant, startFen, moves, uci);
      if (!played) throw new Error(`illegal bot move ${uci}`);
      current.addMove(played.move, played.position.result);
      sound(played.position.result ? "end" : played.move.check ? "check" : played.move.captured ? "capture" : "move");
    })().catch(() => { if (!controller.signal.aborted) setEngineError("Your opponent couldn’t respond. Retry to continue this game."); });
    return () => controller.abort();
  }, [hydrated, ff, live, s.status, s.gameId, s.startFen, uciMoves, botColor, variant, level, engineAttempt]);

  const onMove = useCallback((intent: MoveIntent) => {
    if (!ff || !live || !playerTurn || s.cursor !== null) return false;
    const uci = intentToUci(live.legal, intent);
    if (!uci) return false;
    const played = playVariantMove(ff, variant, s.startFen, uciMoves, uci);
    if (!played) return false;
    setDrop(null);
    s.addMove(played.move, played.position.result);
    sound(played.position.result ? "end" : played.move.check ? "check" : played.move.captured ? "capture" : "move");
    return true;
  }, [ff, live, playerTurn, s, variant, uciMoves]);

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey || document.querySelector("dialog[open]")) return;
      if ((event.target as HTMLElement).closest("input, textarea, select, button, [role=grid], [contenteditable=true]")) return;
      const current = useVariantStore.getState();
      if (current.status === "setup") return;
      const at = current.cursor ?? current.moves.length;
      const next = event.key === "ArrowLeft" ? Math.max(0, at - 1) : event.key === "ArrowRight" ? Math.min(current.moves.length, at + 1) : event.key === "Home" ? 0 : event.key === "End" ? current.moves.length : null;
      if (next === null) return;
      event.preventDefault(); current.setCursor(next === current.moves.length ? null : next);
    };
    window.addEventListener("keydown", key); return () => window.removeEventListener("keydown", key);
  }, []);

  const atLive = s.cursor === null;
  const viewCheckSquare = atLive ? live?.checkSquare ?? null : s.moves[ply - 1]?.check ? findKing(viewFen, sideToMove(viewFen)) : null;
  const rules = useMemo<BoardRules>(() => ({
    legal: playerTurn && atLive && live ? live.legal : [],
    checkSquare: viewCheckSquare,
    dropPiece: drop,
    onDropCancel: () => setDrop(null),
  }), [playerTurn, atLive, live, viewCheckSquare, drop]);
  const tints = useMemo(() => Object.fromEntries((variant.zone?.squares ?? []).map((sq) => [sq, ZONE_TINT])) as Partial<Record<Square, string>>, [variant]);
  const pockets = variant.pockets && viewFen ? parsePockets(viewFen) : null;
  const checksLeft = variant.id === "threecheck" && viewFen ? checksRemaining(viewFen) : null;
  const numberingFen = s.startFen ? plainFen(s.startFen) : START_FEN;
  const resultCode = !s.result ? "*" : s.result.winner === "w" ? "1-0" : s.result.winner === "b" ? "0-1" : "1/2-1/2";
  const pgn = useMemo(() => toPgn(s.moves, {
    Event: `Ponderchess ${variant.name} vs bot`,
    Variant: PGN_NAMES[variant.id] ?? variant.name,
    White: s.playerColor === "w" ? "You" : opponent,
    Black: s.playerColor === "b" ? "You" : opponent,
    Result: resultCode,
  }, variant.chess960 ? s.startFen : START_FEN), [s.moves, s.playerColor, s.startFen, variant, opponent, resultCode]);

  if (!hydrated) return <section className="variant-loading" aria-busy="true"><span className="loading-orbit" aria-hidden="true" /><p role="status">Preparing the board…</p></section>;
  if (s.status === "setup" || !s.startFen) return <VariantSetup ff={ff} ffError={ffError} onRetry={retryFfish} />;

  const playerSide = flipped ? botColor : s.playerColor;
  const topSide = playerSide === "w" ? "b" : "w";
  const isYou = (color: "w" | "b") => color === s.playerColor;
  const detailFor = (color: "w" | "b") => {
    const parts: string[] = [];
    if (checksLeft) parts.push(`${3 - checksLeft[color]} of 3 checks given`);
    if (isYou(color)) parts.push(s.takebacks ? "Practice game" : variant.name);
    return parts.join(" · ") || undefined;
  };
  const scoreFor = (color: "w" | "b") => !s.result ? undefined : s.result.winner === null ? "½" : s.result.winner === color ? "1" : "0";
  const resultTitle = !s.result ? "" : s.result.winner === null ? "A game shared." : s.result.winner === s.playerColor ? "The game is yours." : `${opponent} takes the game.`;
  const resultText = !s.result ? "" : s.result.winner === null ? `Draw by ${s.result.reason}.` : `${s.result.winner === s.playerColor ? "You won" : `${opponent} won`} by ${s.result.reason}.`;
  const takebackLen = variantTakebackLength(s.moves, sideToMove(s.startFen), s.playerColor);
  const player = (color: "w" | "b") => <ClubPlayer name={isYou(color) ? "You" : opponent} color={color} active={atLive && s.status === "playing" && (isYou(color) ? playerTurn : thinking)} detail={detailFor(color)} result={scoreFor(color)} />;
  const pocket = (color: "w" | "b") => pockets && <Pocket pockets={pockets} color={color} selectable={isYou(color) && playerTurn && atLive} selected={isYou(color) ? drop : null} onSelect={setDrop} />;

  return <section className="club-play cinema-shell cinema-game variant-game"><div className="club-container">
    <header className="cinema-topbar">
      <Link href="/variants" className="cinema-brand" aria-label="Back to all chess variants"><PlayIcon name="back" /><span>Ponder<span>chess</span></span></Link>
      <div className="cinema-room-name"><span className="club-eyebrow">{variant.name}</span><h1>You <span>vs</span> {opponent}</h1></div>
      <div className="cinema-topbar-actions"><button className="club-secondary" onClick={() => { setResignConfirm(false); setEngineError(null); s.backToSetup(); }}>New game <PlayIcon name="arrow" /></button></div>
    </header>
    {broken ? <div className="variant-broken" role="alert"><p>This saved game can’t be restored.</p><button className="club-primary" onClick={() => s.backToSetup()}>Start a new game</button></div> : <div className="club-game-layout" key={s.gameId}>
      <div className="club-board-column">
        {player(topSide)}
        {pocket(topSide)}
        {playerTurn && atLive && live?.check && <div className="club-board-notice is-check" role="status"><PlayIcon name="spark" /><span>Your king is in check.</span></div>}
        {drop && <div className="club-board-notice" role="status"><PlayIcon name="arrow" /><span>Choose a highlighted square to drop your {PIECE_NAMES[drop]}.</span></div>}
        <PlayBoard fen={viewFen} rules={rules} tints={tints} orientation={(s.playerColor === "w") !== flipped ? "white" : "black"} onFlip={() => setFlipped(!flipped)} movable={playerTurn && atLive && !thinking ? (s.playerColor === "w" ? "white" : "black") : "none"} lastMove={ply > 0 ? { from: s.moves[ply - 1].from as Square, to: s.moves[ply - 1].to as Square } : null} onMove={onMove} label={`${variant.name} board`}>
          <ReplayControls ply={ply} count={s.moves.length} startFen={numberingFen} onSelect={(p) => s.setCursor(p >= s.moves.length ? null : p)} live={!atLive && s.status === "playing"} />
        </PlayBoard>
        {pocket(playerSide)}
        {player(playerSide)}
      </div>
      <aside className="club-game-panel variant-panel" aria-label="Game panel">
        <div className="club-opening"><span>{variant.name}</span><p>{variant.goal}</p></div>
        {s.status === "playing" ? <div className={`club-live-title ${thinking ? "is-thinking" : ""}`}>
          <span className="club-eyebrow">{atLive ? "At the board" : "Replay"}</span>
          <h2>{!atLive ? "Looking back." : engineError ? "Let’s reconnect." : !live ? "Loading the rules…" : thinking ? "Your opponent is thinking." : live.check ? "You’re in check." : "Your turn."}</h2>
          <p>{!atLive ? "Use the controls under the board to return to the game." : pockets && playerTurn ? "Move a piece, or choose one from your pocket to drop it." : "Select a piece to see where it can move."}</p>
        </div> : <div className="club-result" role="status"><span className="club-eyebrow">Game complete · {resultCode.replace("1/2-1/2", "½–½")}</span><h2>{resultTitle}</h2><p>{resultText}</p></div>}
        <ul className="variant-panel-reminders">{variant.reminders.map((r) => <li key={r}>{r}</li>)}</ul>
        <div className="club-section-heading"><span className="club-eyebrow">Moves</span><span className="club-muted">{Math.ceil(s.moves.length / 2)} played</span></div>
        <div className="club-move-headings" aria-hidden="true"><span /><span>White</span><span>Black</span></div>
        <MoveList moves={s.moves} current={ply} onSelect={(p) => s.setCursor(p >= s.moves.length ? null : p)} blackFirst={sideToMove(s.startFen) === "b"} />
        {s.status === "playing" && <>
          <div className="club-game-actions">
            <button className="club-text-button" onClick={() => { s.takeback(); setEngineError(null); }} disabled={takebackLen === null}>Take back</button>
            <button className="club-text-button" aria-expanded={resignConfirm} onClick={() => setResignConfirm(!resignConfirm)}>Resign</button>
          </div>
          {resignConfirm && <div className="club-resign" role="group" aria-label="Confirm resignation"><p>Ready to finish this game?</p><button className="club-secondary" onClick={() => { s.resign(); sound("end"); setResignConfirm(false); }}>Resign game</button><button className="club-text-button" onClick={() => setResignConfirm(false)}>Keep playing</button></div>}
        </>}
        {s.status === "over" && <div className="variant-over-actions"><button className="club-primary" onClick={() => s.backToSetup()}>Play again <PlayIcon name="arrow" /></button><Link className="club-text-button" href={`/variants/${variant.slug}`}>Read about {variant.name}</Link></div>}
        {(engineError || ffError) && <div className="club-error" role="alert"><p>{engineError ?? ffError}</p><button className="club-secondary" onClick={() => { if (ffError) retryFfish(); setEngineError(null); setEngineAttempt((n) => n + 1); }}>Reconnect opponent</button></div>}
        {s.moves.length > 0 && <details className="club-pgn"><summary>Game notation <span>PGN</span></summary><label className="sr-only" htmlFor="variant-pgn">Game PGN</label><textarea id="variant-pgn" readOnly value={pgn} /></details>}
        {variant.chess960 && s.chess960Index !== null && <p className="club-assistance-note">Chess960 starting position {s.chess960Index}.</p>}
      </aside>
    </div>}
    <p className="sr-only" aria-live="polite">{s.moves.length ? `Last move ${s.moves[s.moves.length - 1].san}.` : ""} {resultText}</p>
  </div></section>;
}

